import { ErrorBoundary, ExploreTopBanner } from '@tecton/ComponentRedesign';
import SideNavigation from '@tecton/SideNavigation/SideNavigation';
import { useCallback, useRef, useState } from 'react';
import { GlobalHotKeys } from 'react-hotkeys';
import { Outlet } from 'react-router';
import { Routes } from '../../core/routes';
import { useConfig } from '../context/ConfigContext';
import { logEvent } from '../../utils/analytics-utils';
import { logout } from '../../utils/okta';
import { FeatureFlags, useUserSettings } from '../context/UserSettingsContext';
import GlobalSearch from './GlobalSearch';
import { useHotKeysContext } from '../context/HotKeysContext';
import { ApplicationLayout, IconType, NavigationItemProps } from '@lithos';

const ApplicationContainer = () => {
  const { user, workspace, canShowMainApplication, oktaAuth, allWorkspaces, isPublicFreeTrial, isLoadingWorkspaces } =
    useUserSettings();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [canShowBanner, setCanShowBanner] = useState(true);
  const SearchRef = useRef<HTMLDivElement>(null);
  const { config } = useConfig();
  const { showSearchModal, setShowSearchModal } = useHotKeysContext();
  const { featureEnabled } = useUserSettings();
  const canShowServerGroups = featureEnabled(FeatureFlags.SHOW_SERVER_GROUPS);
  // ------------- Fetch Data

  const foundWorkspace = allWorkspaces?.find((ws) => {
    return ws.name === workspace;
  });

  // -------------------------- Workspace selector

  const getSideNavigationItems = () => {
    const canShowCompute = config.COMPUTE_ENGINES.includes('Spark') && config.SPARK_RUNTIME.includes('DATABRICKS');
    const canShowDataQuality = config.DATA_OBSERVABILITY_VALIDATION_ENABLED === 'true';

    const computeItem: NavigationItemProps[] = canShowCompute
      ? [
          {
            name: 'Compute',
            icon: 'Compute',
            url: Routes.dataPlatformConfiguration,
          },
        ]
      : [];

    const dataQualityItem: NavigationItemProps[] = canShowDataQuality
      ? [
          {
            name: 'Data Quality',
            icon: 'DataQuality',
            url: Routes.validations,
          },
        ]
      : [];

    const serverGroupsItem: NavigationItemProps[] = canShowServerGroups
      ? [
          {
            name: 'Server Groups',
            icon: 'TransformServerGroup' as IconType,
            url: Routes.serverGroupsIndex,
          },
        ]
      : [];

    const workspaceItems: NavigationItemProps[] =
      canShowMainApplication && !isLoadingWorkspaces
        ? [
            {
              name: 'Dataflow',
              icon: 'Dataflow',
              url: Routes.dashboard,
            },
            {
              name: 'Sources',
              icon: 'Sources',
              url: Routes.dataSources,
            },
            {
              name: 'Entities',
              icon: 'Entities',
              url: Routes.entities,
            },
            {
              name: 'Transformations',
              icon: 'Transformations',
              url: Routes.transformations,
            },
            {
              name: 'Features',
              icon: 'FeatureView',
              url: Routes.featureViews,
            },
            {
              name: 'Services',
              icon: 'FeatureService',
              url: Routes.featureServices,
            },
            {
              name: 'Datasets',
              icon: 'Dataset',
              url: Routes.savedFeatureDataFrames,
            },
            {
              name: 'Permissions',
              icon: 'PermissionsIcon',
              url: Routes.configurations,
            },
            ...serverGroupsItem,
            ...dataQualityItem,
          ]
        : [
            {
              name: 'Workspaces',
              icon: 'Workspaces',
              url: `/repo/*`,
            },
            ...computeItem,
            {
              name: 'Jobs',
              icon: 'Jobs',
              url: Routes.jobs,
            },
            {
              name: 'Accounts & Access',
              icon: 'AccountsAndAccess',
              url: Routes.accountAndAccess,
            },
          ];

    const generalItems: NavigationItemProps[] = canShowMainApplication
      ? [
          ...computeItem,
          {
            name: 'Jobs',
            icon: 'Jobs',
            url: Routes.jobs,
          },
          {
            name: 'Monitoring',
            icon: 'Monitoring',
            url: Routes.monitoring,
          },
          {
            name: 'Accounts & Access',
            icon: 'AccountsAndAccess',
            url: Routes.accountAndAccess,
          },
        ]
      : [];

    return { workspaceItems, generalItems };
  };

  const { workspaceItems, generalItems } = getSideNavigationItems();

  // ----------------------- callBack Handlers ----------------

  const keyMap = {
    GLOBAL_SEARCH: 'command+k',
  };

  const handlers = {
    GLOBAL_SEARCH: (event: any) => {
      event.preventDefault();
      setShowSearchModal(true);
      logEvent('Search', '', { action: 'command+k', description: 'Show search modal' });
    },
  };

  const onLogoutClick = () => {
    logout(oktaAuth);
  };

  const leftSide = (
    <>
      <GlobalHotKeys keyMap={keyMap} handlers={handlers} />
      <SideNavigation
        setShowSearchModal={setShowSearchModal}
        generalItems={generalItems}
        workspaceItems={workspaceItems}
      />

      {showSearchModal && (
        <>
          <GlobalSearch
            SearchRef={SearchRef}
            setShowSearchModal={setShowSearchModal}
            workspace={foundWorkspace as any}
          />
        </>
      )}
    </>
  );

  return (
    <>
      <ApplicationLayout
        navigation={leftSide}
        content={
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        }
        banner={
          isPublicFreeTrial &&
          canShowBanner && (
            <ExploreTopBanner
              onClick={() => {
                setCanShowBanner(false);
              }}
            />
          )
        }
      />
      {isPublicFreeTrial && canShowBanner && (
        <ExploreTopBanner
          onClick={() => {
            setCanShowBanner(false);
          }}
        />
      )}
    </>
  );
};

export default ApplicationContainer;
